import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      <section className="bg-gradient-to-br from-[#002029] via-[#00303D] to-[#004052] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-300">
              Last updated: December 2024
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Agreement to Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  By accessing and using the Venura Consultants website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing our services.
                </p>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Services Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Venura Consultants provides:</p>
                <ul className="list-disc pl-6">
                  <li>Financial consulting and advisory services</li>
                  <li>Accounting and compliance solutions</li>
                  <li>Management reporting and automation</li>
                  <li>Due diligence support</li>
                  <li>Operational advisory services</li>
                </ul>
                <p className="mt-4">
                  The specific scope of services will be defined in individual service agreements or engagement letters with clients.
                </p>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Use License</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Permission is granted to temporarily access the materials (information or software) on Venura Consultants' website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:</p>
                <ul className="list-disc pl-6">
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for any commercial purpose</li>
                  <li>Attempt to decompile or reverse engineer any software contained on our website</li>
                  <li>Remove any copyright or proprietary notations from the materials</li>
                  <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Professional Advice Disclaimer</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  The information provided on our website and through our blog articles is for general informational purposes only. While we strive to keep the information up to date and correct, we make no representations or warranties of any kind about the completeness, accuracy, reliability, or suitability of the information.
                </p>
                <p className="mt-4">
                  <strong>This information should not be construed as professional financial, accounting, or legal advice.</strong> You should consult with appropriate professionals for advice specific to your situation.
                </p>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Client Engagement Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <h3 className="text-lg font-semibold mt-4 mb-2">Engagement Letters</h3>
                <p>
                  All client engagements require a signed engagement letter or service agreement that specifies the scope of services, fees, timelines, and other relevant terms.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-2">Fees and Payment</h3>
                <p>Fees for services will be specified in the engagement letter. Payment terms typically include:</p>
                <ul className="list-disc pl-6">
                  <li>Fees may be project-based, hourly, or monthly retainer</li>
                  <li>Payment is generally due within 30 days of invoice date</li>
                  <li>Late payments may incur interest charges</li>
                  <li>We reserve the right to suspend services for non-payment</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-2">Client Responsibilities</h3>
                <p>Clients are responsible for:</p>
                <ul className="list-disc pl-6">
                  <li>Providing timely access to requested information and documentation</li>
                  <li>Ensuring the accuracy of information provided</li>
                  <li>Making decisions based on our advice and recommendations</li>
                  <li>Implementing recommended changes to systems and processes</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Confidentiality</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  We maintain strict confidentiality regarding all client information. We will not disclose confidential information to third parties without client consent, except as required by law or professional standards.
                </p>
                <p className="mt-4">
                  Clients are also expected to maintain confidentiality regarding our proprietary methodologies, tools, and recommendations.
                </p>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Intellectual Property</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  All content on this website, including text, graphics, logos, and software, is the property of Venura Consultants and protected by copyright and trademark laws. Our proprietary methodologies, frameworks, and tools remain our intellectual property even when applied to client work.
                </p>
                <p className="mt-4">
                  Deliverables created specifically for clients become client property upon full payment, subject to the terms of the engagement letter.
                </p>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Limitation of Liability</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  In no event shall Venura Consultants or its principals be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use our services or materials, even if we have been notified orally or in writing of the possibility of such damage.
                </p>
                <p className="mt-4">
                  Our maximum liability under any engagement shall not exceed the fees paid by the client for the specific services giving rise to the liability.
                </p>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Termination</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Either party may terminate an engagement according to the terms specified in the engagement letter. Typically:</p>
                <ul className="list-disc pl-6">
                  <li>Either party may terminate with written notice (usually 30 days)</li>
                  <li>Clients remain responsible for fees incurred through the termination date</li>
                  <li>We will provide work completed through the termination date</li>
                  <li>Confidentiality obligations continue after termination</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Website Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <h3 className="text-lg font-semibold mt-4 mb-2">Third-Party Links</h3>
                <p>
                  Our website may contain links to third-party websites. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-2">User Submissions</h3>
                <p>
                  Any information or materials you submit through our website (contact forms, consultation requests) may be used by us for business purposes, subject to our Privacy Policy.
                </p>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Governing Law</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  These Terms shall be governed by and construed in accordance with applicable laws, without regard to its conflict of law provisions. Any disputes arising from these Terms or our services shall be resolved through arbitration or in the appropriate jurisdiction's courts.
                </p>
              </CardContent>
            </Card>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Changes to Terms</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  We reserve the right to modify these Terms at any time. We will notify clients of any material changes. Continued use of our services after changes constitutes acceptance of the modified Terms.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <p>If you have questions about these Terms of Service, please contact us:</p>
                <div className="mt-4">
                  <p><strong>Email:</strong> admin@venuraconsultants.com</p>
                  <p><strong>Website:</strong> www.venuraconsultants.com</p>
                  <p><strong>LinkedIn:</strong> <a href="https://www.linkedin.com/company/108459329/" target="_blank" rel="noopener noreferrer" className="text-[#00607A] hover:underline">Venura Consultants</a></p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;